import type { ExpAdmission, ExpChange, ExpDisplayUpdate, ExpExactEvidence, ExpPoint, GameEvent, GameStateSnapshot, ValidatedObservation } from '@/domain/types'
import { expMatchesRequirement, expRequirementRange, intersectExpRequirementRanges, representativeExpRequirement, type ExpRequirementRange } from './exp-requirement-estimator'
import { EXP_TRANSITION_REVIEW_MS, freshLevel, lossMatchesLevel } from './exp-transition'

export const EXP_JOURNEY_REVIEW_MS = EXP_TRANSITION_REVIEW_MS

export function exactSessionExpPoint(state: GameStateSnapshot | null): ExpPoint | null {
  if (!state || state.level === null || state.exp.absolute === null || state.exp.ratio === null
    || state.exp.estimated || !Number.isInteger(state.level) || !Number.isSafeInteger(state.exp.absolute)
    || state.exp.absolute < 0 || !expMatchesRequirement(state.exp, state.exp.requiredExp ?? null)) return null
  return { level: state.level, absolute: state.exp.absolute, ratio: state.exp.ratio,
    percentageRounding: state.exp.percentageRounding, sourceId: state.exp.sourceId, percentagePrecision: state.exp.percentagePrecision, requiredExp: state.exp.requiredExp ?? null,
    requiredExpApproximate: state.exp.requiredExpApproximate ?? state.exp.requiredExp != null }
}

interface Cycle {
  level: number
  offset: number
  range: ExpRequirementRange | null
  required: number | null
  anchor: ExpPoint
  wrap?: { point: ExpPoint; at: number; required: number | null }
}

interface JourneyReview {
  id: string
  revision: number
  fromSourceAt: number
  before: ExpPoint
  range: ExpRequirementRange | null
  lastSourceAt: number | null
  samples: number
  correctionFromSourceAt?: number
  publishedEndpoint: boolean
}

/** Runtime-only proof that this review never submitted a suspect endpoint to history. */
export interface UncommittedExpReview {
  readonly fromSourceAt: number
  readonly status: 'pending' | 'discarded'
}

interface TransitionReview {
  level: number
  range: ExpRequirementRange | null
  sourceAt: number
  count: number
}

interface CaptureReconnection {
  since: number
  boundary: number
  last?: { sourceAt: number; level: number; absolute: number; range: ExpRequirementRange | null }
  samples: number
}

/** Displayed net earnings are recomputed from fixed segment endpoints, never from admitted gains. */
export class SessionExpCounter {
  private startAbsolute: number | null = null
  private closedGain: number | null = 0
  private cycles: Cycle[] = []
  private review: TransitionReview | null = null
  private minimumSourceAt = -Infinity
  private endSourceAt: number | null = null
  private segmentPending = false
  private closedApproximate = false
  private display: ExpDisplayUpdate = this.empty()
  private journeyReview: JourneyReview | null = null
  private levelEvidence: { level: number; sourceAt: number } | null = null
  private incomplete = false
  private incompleteAt: number | null = null
  private reconnection: CaptureReconnection | null = null
  private continuityGap = false
  private nextPointDiscontinuous = false
  private confirmedGain = 0
  private recentLoss: { review: JourneyReview; last: ExpPoint } | null = null
  private admission: ExpAdmission | undefined
  private timelineInvalidated = false
  private pendingEndpointSince: number | null = null
  private expiredCandidateReview: JourneyReview | null = null
  private discardedUncommittedReview: UncommittedExpReview | undefined

  constructor(private readonly onTimelineUpdate?: (display: ExpDisplayUpdate, invalidated: boolean, uncommitted?: UncommittedExpReview) => void) {}

  private publishTimeline(): void {
    const display = this.snapshot(), review = this.journeyReview
    const pendingSince = review?.fromSourceAt ?? this.pendingEndpointSince
    const uncommitted = !this.timelineInvalidated && !this.incomplete && !this.reconnection && !this.continuityGap
      ? pendingSince != null && !review?.publishedEndpoint && display.sourceAt !== null && display.sourceAt < pendingSince
        ? { fromSourceAt: pendingSince, status: 'pending' as const }
        : this.discardedUncommittedReview
      : undefined
    this.onTimelineUpdate?.(display, this.timelineInvalidated, uncommitted)
    if (review && display.sourceAt !== null && display.sourceAt >= review.fromSourceAt) review.publishedEndpoint = true
    this.timelineInvalidated = false
  }

  reset(state: GameStateSnapshot | null = null, minimumSourceAt = -Infinity): void {
    this.timelineInvalidated = false
    this.startAbsolute = null
    this.pendingEndpointSince = null
    this.expiredCandidateReview = null
    this.discardedUncommittedReview = undefined
    this.closedGain = 0
    this.cycles = []
    this.review = null
    this.minimumSourceAt = -Infinity
    this.endSourceAt = null
    this.segmentPending = false
    this.closedApproximate = false
    this.display = this.empty()
    this.journeyReview = null
    this.levelEvidence = null
    this.incomplete = false
    this.incompleteAt = null
    this.reconnection = null
    this.continuityGap = false
    this.nextPointDiscontinuous = false
    this.confirmedGain = 0
    this.recentLoss = null
    this.admission = undefined
    if (state) this.ingestState(state)
    this.minimumSourceAt = minimumSourceAt
  }

  breakContinuity(at: number): void {
    this.closePending(at)
    this.pendingEndpointSince = null
    this.expiredCandidateReview = null
    this.discardedUncommittedReview = undefined
    if (!this.segmentPending) {
      this.closedGain = this.incomplete ? this.confirmedGain : this.display.status === 'unavailable' ? 0 : this.display.netGain
      this.closedApproximate = this.display.approximate
    }
    this.startAbsolute = null
    this.cycles = []
    this.review = null
    this.segmentPending = true
    this.minimumSourceAt = at
    this.journeyReview = null
    this.levelEvidence = null
    this.recentLoss = null
    this.admission = undefined
    this.display = { ...this.display, journeyTransition: undefined }
  }

  /** Only an automatic restart of the same capture may bridge back to the old origin. */
  reconnectCapture(at: number): void {
    this.expireJourney(at)
    if (this.incomplete || this.journeyReview || this.display.status === 'unresolved-level'
      || this.startAbsolute === null || !this.display.point) {
      this.invalidateContinuity(at)
      return
    }
    this.reconnection = { since: this.reconnection?.since ?? at, boundary: at, samples: 0 }
    this.minimumSourceAt = at
    this.levelEvidence = null
    this.recentLoss = null
    this.review = null
    this.display = { ...this.display, journeyTransition: undefined }
  }

  /** Unknown sources cannot silently discard a gap and still report a complete total. */
  invalidateContinuity(at: number): void {
    this.incomplete = true
    this.incompleteAt ??= this.reconnection?.since ?? at
    this.continuityGap = true
    this.breakContinuity(at)
  }

  ingestObservation(observation: ValidatedObservation, minimumConfidence: number): void {
    this.prepareJourney(observation.capturedAt)
    if (observation.accepted.level && observation.level.value !== null
      && observation.level.confidence >= minimumConfidence) {
      this.observeLevel(observation.level.value, observation.level.capturedAt, observation.capturedAt)
    }
    const candidates: { sample: ExpExactEvidence; confirmedUpgrade: boolean; admission?: ExpAdmission; accepted: boolean }[] = []
    const evidence = observation.expExactEvidence
    if (evidence) candidates.push({ sample: evidence, admission: observation.expEvidenceAdmission,
      accepted: observation.expEvidenceAdmission ? (observation.expEvidenceAdmission.displayStatus ?? observation.expEvidenceAdmission.status) === 'accepted' : observation.expExactEvidenceAccepted === true,
      confirmedUpgrade: observation.expEvidenceAdmission?.transition === 'level-up'
        || (!observation.expEvidenceAdmission && observation.expExactEvidenceAccepted === true) })
    const candidate = observation.expExactCandidate
    if (candidate) candidates.push({ sample: { ...candidate, confirmation: 'source-frame' }, admission: observation.expCandidateAdmission,
      accepted: (observation.expCandidateAdmission?.displayStatus ?? observation.expCandidateAdmission?.status) === 'accepted' && candidate.barConsistency !== 'conflict',
      confirmedUpgrade: observation.expCandidateAdmission?.transition === 'level-up' })
    candidates.sort((a, b) => a.sample.sourceAt - b.sample.sourceAt)
    const before = this.endSourceAt
    for (const { sample, confirmedUpgrade, admission, accepted } of candidates) {
      this.admission = admission
      this.observe(sample, minimumConfidence, confirmedUpgrade, accepted)
      // Publish each candidate: a later candidate can replace the display's correction metadata.
      this.publishTimeline()
    }
    this.updateJourneyReview()
    this.publishTimeline()
    if (before === this.endSourceAt && observation.capturedAt > (this.endSourceAt ?? -Infinity)
      && this.display.journeyTransition?.status !== 'confirmed') {
      this.display = { ...this.display, status: this.display.status === 'ready' ? 'stale' : this.display.status }
    }
  }

  ingestState(state: GameStateSnapshot): void {
    this.admission = undefined
    this.prepareJourney(state.capturedAt)
    if (state.level !== null) this.observeLevel(state.level, state.observedAt?.level ?? state.capturedAt, state.capturedAt)
    const point = exactSessionExpPoint(state)
    if (!point) { this.publishTimeline(); return }
    this.observe({ level: point.level, value: state.exp, sourceAt: state.observedAt?.exp ?? state.capturedAt,
      deliveredAt: state.capturedAt, confidence: state.confidence.exp, percentagePrecision: state.exp.percentagePrecision,
      confirmation: 'consensus' }, .62, true)
    this.publishTimeline()
  }

  snapshot(): ExpDisplayUpdate {
    if (this.incomplete) return { ...this.display, netGain: null, status: 'unresolved-level' }
    if (this.reconnection) return { ...this.display, pendingNetGain: this.display.netGain, netGain: null, status: 'pending-transition' }
    if ((this.journeyReview || this.pendingEndpointSince !== null) && this.display.status !== 'unresolved-level') {
      return { ...this.display, pendingNetGain: this.display.netGain, netGain: null, status: 'pending-transition' }
    }
    return this.display
  }
  total(): number | null { return this.snapshot().netGain }
  isIncomplete(): boolean { return this.incomplete }
  incompleteSince(): number | null { return this.incompleteAt }
  reviewReason(): 'waiting-level' | 'missing-levels' | 'checking-change' | 'continuity-break' | null {
    if (this.reconnection || this.continuityGap) return 'continuity-break'
    if (!this.journeyReview) return this.pendingEndpointSince === null ? null : 'checking-change'
    if (!this.levelEvidence || this.levelEvidence.sourceAt < this.journeyReview.fromSourceAt) return 'waiting-level'
    return this.display.status === 'unresolved-level' ? 'missing-levels' : 'checking-change'
  }
  approximate(): boolean { return this.display.approximate }
  latestSourceAt(): number | null { return this.endSourceAt }

  closePending(at = this.endSourceAt ?? 0): void {
    this.pendingEndpointSince = null
    this.expiredCandidateReview = null
    if (this.reconnection || this.journeyReview || this.display.status === 'unresolved-level') {
      this.incomplete = true
      this.incompleteAt ??= this.reconnection?.since ?? at
      this.continuityGap ||= this.reconnection !== null
    }
    this.reconnection = null
    this.journeyReview = null
    this.recentLoss = null
    this.review = null
    if (this.incomplete) this.display = { ...this.display, netGain: null, status: 'unresolved-level', journeyTransition: undefined }
  }

  confirmLevelUp(events: readonly GameEvent[]): void {
    if (this.reconnection) return
    const cycle = this.cycles.at(-1)
    const point = this.display.point
    const event = events.find(event => event.type === 'level-up' && event.from === cycle?.level && event.to === event.from! + 1)
    if (!cycle || !point || !event || cycle.required === null || !cycle.wrap
      || event.capturedAt < cycle.wrap.at || event.capturedAt - cycle.wrap.at > EXP_JOURNEY_REVIEW_MS
      || this.display.netGain === null || !this.levelIsFresh(event.to!, cycle.wrap.at)) return
    const required = cycle.wrap.required ?? cycle.required
    this.cycles.push({ level: event.to!, offset: cycle.offset + required, range: null, required: null,
      anchor: { ...point, level: event.to! } })
    this.display = { ...this.display, point: { ...point, level: event.to! }, netGain: this.display.netGain + required,
      approximate: true, status: 'ready', levelUp: { from: event.from!, to: event.to! } }
    this.review = null
    this.updateJourneyReview()
    this.publishTimeline()
  }

  private observe(sample: ExpExactEvidence, minimumConfidence: number, confirmedUpgrade: boolean, accepted = true): void {
    const { absolute, ratio } = sample.value
    const range = expRequirementRange(sample.value, sample.percentagePrecision)
    const currentCycle = this.cycles.at(-1)
    const wrap = currentCycle?.wrap
    if (wrap && sample.level === currentCycle.level + 1 && sample.sourceAt - wrap.at <= EXP_JOURNEY_REVIEW_MS
      && this.levelIsFresh(sample.level, this.journeyReview?.fromSourceAt ?? wrap.at)
      && ((absolute === 0 && ratio === 0) || (range != null && !this.compatible(currentCycle.range, range)))) {
      confirmedUpgrade = true
    }
    if (sample.level === null || !Number.isInteger(sample.level) || sample.level < 1 || sample.level > 200
      || absolute === null || !Number.isSafeInteger(absolute) || absolute < 0 || ratio === null
      || !Number.isFinite(ratio) || ratio < 0 || ratio > 1 || sample.value.estimated || range === undefined
      || !Number.isFinite(sample.confidence) || sample.confidence < minimumConfidence
      || !Number.isFinite(sample.sourceAt) || !Number.isFinite(sample.deliveredAt)
      || sample.sourceAt > sample.deliveredAt || sample.sourceAt < this.minimumSourceAt
      || sample.sourceAt < (this.endSourceAt ?? -Infinity)
      || (sample.sourceAt === this.endSourceAt && (!confirmedUpgrade || sample.level === this.display.point?.level))) return

    if (accepted && this.startAbsolute !== null && this.display.point?.sourceId !== sample.value.sourceId) {
      this.breakContinuity(sample.sourceAt)
      return this.observe(sample, minimumConfidence, false, true)
    }
    if (!accepted) {
      if (this.admission?.status === 'rejected' && this.admission.reason === 'stale') return
      this.pendingEndpointSince ??= sample.sourceAt
      const before = this.display.point
      if (!this.reconnection && this.startAbsolute !== null && !this.journeyReview && before
        && (absolute < before.absolute || ratio < before.ratio - .0002 || sample.level !== before.level)) {
        this.beginJourneyReview(before, sample.sourceAt)
      }
      return
    }
    const expired = this.expiredCandidateReview
    if (expired && (this.endSourceAt ?? -Infinity) < expired.fromSourceAt && !this.continuityGap
      && sample.level === expired.before.level && absolute >= expired.before.absolute
      && ratio >= expired.before.ratio - .0002
      && this.compatible(expRequirementRange(expired.before) ?? null, range)) {
      // The expired review never adopted a candidate. A compatible same-level
      // endpoint proves that the original display segment can continue.
      this.incomplete = false
      this.incompleteAt = null
      this.journeyReview = expired
    }
    this.expiredCandidateReview = null
    this.pendingEndpointSince = null
    const independentlyReviewed = this.admission?.displayStatus === 'accepted' && this.admission.samples >= 3
    const baseline = this.admission?.displayBaseline ?? this.admission?.rebaseline
    if (this.startAbsolute === null && baseline && baseline.sourceAt >= this.minimumSourceAt && baseline.sourceAt < sample.sourceAt) {
      // Confirmation authorizes the first endpoint too. Otherwise resuming
      // would silently lose all legitimate gains collected during the review.
      this.observe({ ...baseline, deliveredAt: sample.deliveredAt }, minimumConfidence, false, true)
      this.publishTimeline()
    }
    let reconnectedLossRange: ExpRequirementRange | null | undefined
    if (this.reconnection) {
      const pending = this.reconnection
      if (!accepted || !this.confirmReconnection(sample, range ?? null)) return
      if (sample.level === currentCycle!.level && absolute < this.display.point!.absolute) {
        reconnectedLossRange = pending.last!.range
      }
      confirmedUpgrade ||= sample.level === currentCycle!.level + 1
    }
    const required = range && range.maximum !== Number.MAX_SAFE_INTEGER ? representativeExpRequirement(range, absolute, sample.value.requiredExp) : null
    const point: ExpPoint = { level: sample.level, absolute, ratio, requiredExp: required,
      requiredExpApproximate: required !== null, percentageRounding: sample.value.percentageRounding, sourceId: sample.value.sourceId, percentagePrecision: sample.percentagePrecision ?? 2 }
    this.revisitLoss(point, sample, this.admission?.change)
    const before = this.display.point
    if (this.startAbsolute !== null && !this.journeyReview && before && (this.admission?.change?.status === 'pending'
      || point.absolute < before.absolute || point.ratio < before.ratio - .0002
      || point.level !== before.level)) this.beginJourneyReview(before, sample.sourceAt)
    const journeyReview = this.journeyReview
    if (journeyReview && this.admission?.confirmedLoss) {
      journeyReview.samples = 2
      journeyReview.range = range
    }
    if (journeyReview && reconnectedLossRange !== undefined) {
      // The gate already checked two preceding accepted loss endpoints. Preserve that
      // evidence: the upstream confirmedLoss flag may have appeared on only the first one.
      journeyReview.samples = 2
      journeyReview.range = reconnectedLossRange
      if (this.admission) this.admission = { ...this.admission, confirmedLoss: true }
    }
    if (journeyReview && journeyReview.correctionFromSourceAt === undefined
      && (point.absolute < journeyReview.before.absolute || point.ratio < journeyReview.before.ratio - .0002
        || (point.level !== journeyReview.before.level && range != null
          && !this.compatible(expRequirementRange(journeyReview.before) ?? null, range)))) {
      journeyReview.correctionFromSourceAt = sample.sourceAt
      journeyReview.fromSourceAt = Math.min(journeyReview.fromSourceAt, sample.sourceAt)
    }
    this.endSourceAt = sample.sourceAt
    let continuity: ExpDisplayUpdate['continuity'] = this.nextPointDiscontinuous ? 'discontinuous' : 'contiguous'
    this.nextPointDiscontinuous = false
    let levelUp: ExpDisplayUpdate['levelUp']
    if (this.startAbsolute === null) {
      this.startAbsolute = absolute
      this.cycles = [{ level: point.level, offset: 0, range, required, anchor: point }]
      continuity = this.segmentPending ? 'discontinuous' : 'baseline'
      this.segmentPending = false
    } else {
      let cycle = this.cycles.at(-1)!
      const compatible = this.compatible(cycle.range, range)
      // A corrected level/requirement can invalidate an earlier inferred upgrade.
      let earlierIndex = -1
      for (let index = this.cycles.length - 2; index >= 0; index--) {
        const entry = this.cycles[index]!
        if (entry.range && range && this.compatible(entry.range, range)
          && (point.level === entry.level || (cycle.range !== null && !compatible))) {
          earlierIndex = index
          break
        }
      }
      if (earlierIndex >= 0) {
        if (!independentlyReviewed && !this.confirmTransition(point.level, range, sample.sourceAt)) return this.unresolved(point)
        this.timelineInvalidated = true
        this.cycles = this.cycles.slice(0, earlierIndex + 1)
        cycle = this.cycles.at(-1)!
        cycle.level = point.level
      } else if (point.level !== cycle.level) {
        const anchor = cycle.wrap && sample.sourceAt - cycle.wrap.at <= EXP_JOURNEY_REVIEW_MS ? cycle.wrap.point : cycle.anchor
        const wrapped = absolute < anchor.absolute || ratio < anchor.ratio - .0002
        if (compatible && !(confirmedUpgrade && point.level === cycle.level + 1 && wrapped)) {
          cycle.level = point.level
        } else {
          const sequential = point.level === cycle.level + 1
          const changedRequirement = range !== null && cycle.range !== null && !compatible
          const confirmed = this.levelIsFresh(point.level, this.journeyReview?.fromSourceAt ?? sample.sourceAt)
            && (sequential && wrapped && confirmedUpgrade || independentlyReviewed || this.confirmTransition(point.level, range, sample.sourceAt))
          if (!sequential || cycle.required === null || !confirmed || (!wrapped && !changedRequirement)) {
            return this.unresolved(point)
          }
          levelUp = { from: cycle.level, to: point.level }
          cycle = { level: point.level, offset: cycle.offset + cycle.required, range, required, anchor: point }
          this.cycles.push(cycle)
        }
      }
      this.review = null
      if (point.level === cycle.level && (point.absolute < cycle.anchor.absolute || point.ratio < cycle.anchor.ratio - .0002)
        && !cycle.wrap) {
        cycle.wrap = { point: cycle.anchor, at: sample.sourceAt, required: cycle.required }
      } else if (cycle.wrap && (sample.sourceAt - cycle.wrap.at > EXP_JOURNEY_REVIEW_MS || point.absolute >= cycle.wrap.point.absolute)) {
        cycle.wrap = undefined
      }
      const merged = cycle.range && range ? intersectExpRequirementRanges([cycle.range, range]) : cycle.range ?? range
      if (merged !== undefined) {
        cycle.range = merged
        cycle.required = merged && merged.maximum !== Number.MAX_SAFE_INTEGER ? representativeExpRequirement(merged, absolute, cycle.required) : cycle.required
        cycle.anchor = point
      }
    }
    const cycle = this.cycles.at(-1)!
    this.display = { netGain: this.closedGain === null ? null : this.closedGain + cycle.offset + absolute - this.startAbsolute,
      sourceAt: sample.sourceAt, point, status: this.closedGain === null ? 'unresolved-level' : 'ready',
      approximate: this.closedApproximate || cycle.offset !== 0, continuity, ...(levelUp ? { levelUp } : {}) }
    this.updateJourneyReview(sample.sourceAt)
    if (!this.journeyReview && this.display.netGain !== null && !this.incomplete) this.confirmedGain = this.display.netGain
  }

  private compatible(left: ExpRequirementRange | null, right: ExpRequirementRange | null): boolean {
    return left !== null && right !== null && intersectExpRequirementRanges([left, right]) !== undefined
  }

  private confirmReconnection(sample: ExpExactEvidence, range: ExpRequirementRange | null): boolean {
    const pending = this.reconnection!
    if (sample.sourceAt < pending.boundary || !this.levelIsFresh(sample.level!, pending.boundary)
      || this.levelEvidence!.sourceAt > sample.deliveredAt
      || sample.sourceAt <= (pending.last?.sourceAt ?? -Infinity)) return false
    const previous = pending.last
    const merged = previous?.range && range ? intersectExpRequirementRanges([previous.range, range]) : previous?.range ?? range
    const matches = previous?.level === sample.level && sample.value.absolute! >= previous.absolute && merged !== undefined
    pending.samples = matches ? pending.samples + 1 : 1
    pending.last = { sourceAt: sample.sourceAt, level: sample.level!, absolute: sample.value.absolute!, range: matches ? merged! : range }
    if (pending.samples < 3) return false
    const cycle = this.cycles.at(-1)!
    const sameLevel = sample.level === cycle.level
      && (this.compatible(cycle.range, pending.last.range) || sample.value.absolute === 0 || cycle.anchor.absolute === 0)
    const oneLevelUp = sample.level === cycle.level + 1 && cycle.required !== null
      && (sample.value.absolute === 0 || range !== null && !this.compatible(cycle.range, range))
    if (!sameLevel && !oneLevelUp) {
      this.invalidateContinuity(sample.sourceAt)
      return false
    }
    this.reconnection = null
    this.nextPointDiscontinuous = true
    return true
  }

  private confirmTransition(level: number, range: ExpRequirementRange | null, sourceAt: number): boolean {
    const previous = this.review
    const merged = previous?.range && range ? intersectExpRequirementRanges([previous.range, range]) : range
    const matches = previous && previous.level === level && merged !== undefined
      && sourceAt > previous.sourceAt && sourceAt - previous.sourceAt <= 120_000
    this.review = { level, range: merged ?? range, sourceAt, count: matches ? previous.count + 1 : 1 }
    return this.review.count >= 3
  }

  private unresolved(point: ExpPoint): void {
    this.display = { netGain: null, point, sourceAt: this.endSourceAt, status: 'unresolved-level',
      approximate: this.closedApproximate, continuity: 'discontinuous' }
    this.updateJourneyReview()
  }

  expireJourney(now: number): void {
    if (this.reconnection && now - this.reconnection.since > EXP_JOURNEY_REVIEW_MS) this.invalidateContinuity(now)
    if (this.recentLoss && now - this.recentLoss.review.fromSourceAt > EXP_JOURNEY_REVIEW_MS) this.recentLoss = null
    const review = this.journeyReview
    if (!review || now - review.fromSourceAt <= EXP_JOURNEY_REVIEW_MS) return
    // Keep enough evidence to undo an expired review if an unchanged trusted
    // segment is later recovered. Unknown cross-level gaps remain incomplete.
    if (!this.incomplete && (this.endSourceAt ?? -Infinity) < review.fromSourceAt) this.expiredCandidateReview = review
    this.incomplete = true
    this.incompleteAt ??= review.fromSourceAt + EXP_JOURNEY_REVIEW_MS
    this.display = { ...this.display, journeyTransition: {
      status: 'unavailable', fromSourceAt: review.fromSourceAt, toSourceAt: this.endSourceAt ?? review.fromSourceAt,
    } }
    this.journeyReview = null
    const cycle = this.cycles.at(-1)
    if (cycle) cycle.wrap = undefined
  }

  private prepareJourney(now: number): void {
    this.discardedUncommittedReview = undefined
    if (this.display.journeyTransition?.status !== 'pending') this.display = { ...this.display, journeyTransition: undefined }
    this.expireJourney(now)
  }

  private observeLevel(level: number, sourceAt: number, deliveredAt: number): void {
    if (!Number.isInteger(level) || !Number.isFinite(sourceAt) || sourceAt < this.minimumSourceAt
      || sourceAt > deliveredAt || sourceAt <= (this.levelEvidence?.sourceAt ?? -Infinity)) return
    this.levelEvidence = { level, sourceAt }
    const point = this.display.point
    if (this.startAbsolute !== null && !this.journeyReview && point && level !== point.level) this.beginJourneyReview(point, sourceAt)
  }

  private levelIsFresh(level: number, since: number): boolean {
    return freshLevel(this.levelEvidence, level, since)
  }

  private beginJourneyReview(before: ExpPoint, sourceAt: number): void {
    this.journeyReview = { id: String(sourceAt), revision: 0, fromSourceAt: sourceAt, before, range: null, lastSourceAt: null, samples: 0, publishedEndpoint: false }
    this.display = { ...this.display, journeyTransition: {
      status: 'pending', fromSourceAt: sourceAt, toSourceAt: this.endSourceAt ?? sourceAt,
    } }
  }

  private updateJourneyReview(sourceAt?: number): void {
    const review = this.journeyReview
    const { point, netGain, levelUp } = this.display
    if (!review || !point) return
    const at = this.endSourceAt ?? review.fromSourceAt
    const range = expRequirementRange(point, point.percentagePrecision)
    if (sourceAt !== undefined && sourceAt >= review.fromSourceAt && sourceAt > (review.lastSourceAt ?? -Infinity)) {
      if (point.level === review.before.level && point.absolute < review.before.absolute) {
        const merged = review.range && range ? intersectExpRequirementRanges([review.range, range]) : range
        review.samples = merged === undefined ? 1 : review.samples + 1
        review.range = merged ?? range ?? null
      } else {
        review.samples = 0
        review.range = null
      }
      review.lastSourceAt = sourceAt
    }
    let status: 'pending' | 'confirmed' | 'discarded' = 'pending'
    if (at >= review.fromSourceAt && netGain !== null && this.display.status !== 'unresolved-level') {
      if (levelUp || point.level !== review.before.level) status = 'confirmed'
      else if (this.levelEvidence?.level === point.level
        && point.absolute >= review.before.absolute && point.ratio >= review.before.ratio - .0002) status = 'discarded'
      else if (this.admission?.change?.status === 'discarded') status = 'discarded'
      else if (review.samples >= 3 && this.levelIsFresh(point.level, review.fromSourceAt)
        && lossMatchesLevel(review.before, point)
        && (!this.admission || this.admission.confirmedLoss === true)) status = 'confirmed'
    }
    const kind = levelUp ? 'level-up' : status === 'confirmed' && point.level === review.before.level ? 'loss' : 'correction'
    if (status === 'discarded' && !review.publishedEndpoint && this.display.continuity === 'contiguous'
      && point.level === review.before.level && point.sourceId === review.before.sourceId
      && point.absolute >= review.before.absolute && point.ratio >= review.before.ratio - .0002
      && this.compatible(expRequirementRange(review.before) ?? null, range ?? null)) {
      this.discardedUncommittedReview = { fromSourceAt: review.fromSourceAt, status: 'discarded' }
    }
    this.display = { ...this.display, journeyTransition: {
      id: review.id, revision: status === 'pending' ? review.revision : review.revision + 1, kind,
      status, fromSourceAt: review.fromSourceAt, toSourceAt: at,
      ...(status === 'confirmed' ? { netOffset: netGain! - point.absolute, level: point.level,
        correctionFromSourceAt: levelUp ? review.correctionFromSourceAt ?? review.fromSourceAt : review.fromSourceAt,
        ...(levelUp ? { levelUp } : {}) } : {}),
    } }
    if (status !== 'pending') {
      if (kind === 'loss') this.recentLoss = { review: { ...review, revision: review.revision + 1 }, last: point }
      if (netGain !== null && !this.incomplete) this.confirmedGain = netGain
      this.journeyReview = null
      const cycle = this.cycles.at(-1)
      if (cycle) cycle.wrap = undefined
    }
  }

  private revisitLoss(point: ExpPoint, sample: ExpExactEvidence, change?: ExpChange): void {
    const loss = this.recentLoss
    if (!loss || sample.deliveredAt - loss.review.fromSourceAt > EXP_JOURNEY_REVIEW_MS
      || point.level !== loss.review.before.level + 1 || !this.levelIsFresh(point.level, loss.review.fromSourceAt)
      || point.absolute < (this.display.point?.absolute ?? loss.last.absolute)) return
    const sameEndpoint = sample.sourceAt === this.endSourceAt && point.absolute === loss.last.absolute && point.ratio === loss.last.ratio
    const correctedZero = loss.last.absolute === 0 && !lossMatchesLevel(loss.review.before, point)
    if (!(change?.kind === 'level-up' && change.revision > 1) && !sameEndpoint && !correctedZero) return
    this.journeyReview = { ...loss.review }
    const cycle = this.cycles.at(-1)
    if (cycle) cycle.wrap = { point: loss.review.before, at: loss.review.fromSourceAt, required: cycle.required }
    this.recentLoss = null
  }

  private empty(): ExpDisplayUpdate {
    return { netGain: null, point: null, sourceAt: null, status: 'unavailable', approximate: false, continuity: 'baseline' }
  }
}
